// ── Bugify (owner 2026-09-20) ───────────────────────────────────────────────────────────────────
// Firm-wide issue tracker: anyone on staff files a Bug or a Suggestion, an agent on the owner's Mac
// drains the queue every 10 minutes. Exports the inner view (fills its flex parent) plus the page
// wrapper, the house pattern — so a dashboard tab and the /m/bugify route can share one component.
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { tz } from '../../utils/region';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';
import { useStore } from '../../store';
import RichTextEditor from '../RichTextEditor';
import Pagination, { getPageSize } from '../Pagination';
import { brandOr } from '../../utils/brand';
import { openAiSuggestions } from '../AiSuggestions/Digest';   // the AI improvement suggestions' list (report 1492665d)
// Confirm's "deploy now or tonight?" and the deploy's progress (owner 2026-11-03)
import { useRelease, ReleaseHook, ConfirmRelease, ReleaseCard, ReleaseBanner, DeployNowButton, releaseSignal, releaseErrorText } from './Release';

type Item = {
  id: string; kind: string; title: string; status: string;
  awaiting_answer: boolean; asked_user: string; asked_at?: number; pinned: boolean;
  found_version: string; fixed_version: string; fix_commit: string; reverted: boolean;
  preview: string;
  confirmed_by_name?: string; confirmed_at?: number; fanout_due?: boolean;
  release_mode?: string;   // '' | 'now' | 'tonight' — what Confirm chose (Release.tsx)
  wontfix_by_name?: string; wontfix_at?: number; wontfix_reason?: string;
  created_by: string; created_by_name: string; created_at: number; mine: boolean;
  body?: string; attachments?: Att[];
  edited?: boolean; updated_by_name?: string; updated_at?: number; can_edit?: boolean;
  open_count?: number; language?: string; priority?: number; draft?: boolean; night_hold?: boolean;
  patch?: string; fix_diff?: string; patch_files?: string[]; patch_reason?: string; has_patch?: boolean;
  approved_by_name?: string; approved_at?: number; revert_requested?: boolean; revert_by_name?: string;
  can_approve?: boolean; can_revert?: boolean; approval_pending?: boolean; can_publish?: boolean;
  patch_base?: string; code_moved?: boolean; can_unapprove?: boolean;
  overlaps?: { id: string; title: string; files: string[]; approved: boolean }[];
};
type Att = { id: string; name: string; size: number; mime: string; by: string; at: number; comment_id: string };
type Comment = { id: string; kind: string; body: string; by_ai: boolean; created_by_name: string; created_at: number; attachments?: Att[] };

const TITLE_MAX = 64;

// ── What language the AI answers this report in (owner 2026-09-20) ──────────────────────────────
// Per REPORT, not per person: the same colleague files in Chinese one day and English the next, and
// the reply has to match what was ASKED, not who asked. Defaults to the language they are already
// reading the system in — which is itself resolved from the browser — and anything outside these
// three falls back to English, because those are the three the firm works in.
// P0 leads every list; a draft outranks nothing because the AI never sees it at all.
const PRIORITY_LOOK: Record<number, { label: string; fg: string; bg: string }> = {
  0: { label: 'P0', fg: '#B91C1C', bg: '#FEF2F2' },
  1: { label: 'P1', fg: '#B45309', bg: '#FFFBEB' },
  2: { label: 'P2', fg: '#6B7280', bg: '#F3F4F6' },
};
// One compact control for the form's three choices. The first version gave each choice its own
// full-width row with its explanation printed inside every button — three rows of furniture before
// the title field (owner: 控件乱七八糟，请设计得紧凑一些). Now: three labelled segments on one
// line, and ONE muted sentence underneath that explains only what is currently selected.
function Seg({ options, value, onChange }: {
  options: { v: string | number; label: string; fg?: string; bg?: string }[]; value: string | number; onChange: (v: any) => void;
}) {
  return (
    <div style={{ display: 'inline-flex', border: '1px solid var(--border-strong)', borderRadius: 8, overflow: 'hidden', background: 'var(--bg-surface)' }}>
      {options.map((o, i) => {
        const on = o.v === value;
        return (
          <button key={String(o.v)} type="button" onClick={() => onChange(o.v)}
            style={{ border: 'none', borderLeft: i ? '1px solid var(--border)' : 'none', cursor: 'pointer',
              padding: '6px 13px', fontSize: 12.5, fontWeight: on ? 800 : 600, lineHeight: 1,
              background: on ? (o.bg || 'var(--bg-active)') : 'transparent',
              color: on ? (o.fg || 'var(--accent)') : 'var(--text-secondary)' }}>
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

const PriBadge = ({ p }: { p?: number }) => {
  const k = PRIORITY_LOOK[Number.isInteger(p) ? (p as number) : 1] || PRIORITY_LOOK[1];
  return <span style={{ fontSize: 10.5, fontWeight: 900, color: k.fg, background: k.bg, border: `1px solid ${k.fg}44`, borderRadius: 5, padding: '1px 6px', letterSpacing: '.02em', flexShrink: 0 }}>{k.label}</span>;
};

const LANGS: { code: string; label: string }[] = [
  { code: 'en', label: 'English' }, { code: 'zh', label: '中文' }, { code: 'th', label: 'ไทย' },
];
const defaultLang = (uiLang: string) => (LANGS.some(l => l.code === uiLang) ? uiLang : 'en');

// ── Tenant-zone time, in the owner's own format: "3:01pm 20th, Sep, 2026" ───────────────────────────
const ordinal = (d: number) => {
  const s = ['th', 'st', 'nd', 'rd'], v = d % 100;
  return d + (s[(v - 20) % 10] || s[v] || s[0]);
};
export function fmtBkk(ts?: number): string {
  if (!ts) return '—';
  // en-US, not en-GB: the owner's format is "3:01pm 20th, Sep, 2026", and en-GB abbreviates
  // September as "Sept".
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz(), hour: 'numeric', minute: '2-digit', hour12: true,
    day: 'numeric', month: 'short', year: 'numeric',
  }).formatToParts(new Date(ts * 1000));
  const g = (t: string) => parts.find(p => p.type === t)?.value || '';
  return `${g('hour')}:${g('minute')}${g('dayPeriod').toLowerCase().replace(/\s/g, '')} ${ordinal(Number(g('day')))}, ${g('month')}, ${g('year')}`;
}
const relTime = (ts: number): string => {
  if (!ts) return '';
  const s = Math.max(0, Math.floor(Date.now() / 1000) - ts);
  if (s < 90) return `${s}s`;
  if (s < 5400) return `${Math.round(s / 60)}m`;
  if (s < 172800) return `${Math.round(s / 3600)}h`;
  return `${Math.round(s / 86400)}d`;
};

// ── Status colours ──────────────────────────────────────────────────────────────────────────────
// Two of these mean "the ball is with a person", and they are deliberately not the same colour:
// Pink is AI BLOCKED — it cannot go on until someone answers, so it is also pinned and chased after
// 24 hours. Amber (AI Done) is nobody blocked, just nobody has confirmed yet. Green is reserved for
// Confirmed alone: a Won't Fix is closed but NOT done, so it reads as grey and struck through.
type Look = { fg: string; bg: string };
const LOOK: Record<string, Look> = {
  waiting:   { fg: '#4B5563', bg: '#F3F4F6' },
  checking:  { fg: '#1D4ED8', bg: '#EFF6FF' },
  asked:     { fg: '#BE185D', bg: '#FDF2F8' },   // Pink — the AI asked and is waiting
  fixing:    { fg: '#6D28D9', bg: '#F5F3FF' },
  // Written but not shipped, waiting on a named person. Red, because until someone reads it this
  // report is stopped — not progressing slowly, stopped.
  needs_approval: { fg: '#B91C1C', bg: '#FEF2F2' },
  ai_done:   { fg: '#B45309', bg: '#FFFBEB' },
  confirmed: { fg: '#15803D', bg: '#E8F6EE' },
  wont_fix:  { fg: '#6B7280', bg: '#F3F4F6' },
};
// Deploying keeps Fixing's purple: it is the end of the same job — the agent reports 'fixing' right
// through it and the report stays locked — and the word and the 85% already say which part it is at.
const lookOf = (it: { status: string; awaiting_answer: boolean }, deploying = false): Look =>
  (deploying ? LOOK.fixing : it.awaiting_answer ? LOOK.asked : LOOK[it.status]) || LOOK.waiting;
// Status words stay English in every UI language — they are this product's vocabulary, the way
// "Confirmed by Calem" is, and a half-translated state machine is harder to talk about, not easier.
// 'deploying' is never stored on a report; it comes from the agent's heartbeat (see isDeploying).
const STATUS_TEXT: Record<string, string> = {
  waiting: 'Waiting', checking: 'AI Checking', fixing: 'Fixing',
  needs_approval: 'Needs Approval', deploying: 'Deploying', ai_done: 'AI Done', confirmed: 'Confirmed', wont_fix: "Won't Fix",
};
// "Waiting - 2" is the second time this report has been opened (owner 2026-09-20). The first is
// just "Waiting" — a count of one is not worth the ink — but from the second on it is the most
// useful thing the row can say: a report that keeps coming back is one that is not being fixed.
const statusText = (it: { status: string; awaiting_answer: boolean; open_count?: number }, deploying = false) => {
  if (deploying) return STATUS_TEXT.deploying;
  // "AI Checking" when it is thinking; "AI Waiting" when it has stopped and the next move is a
  // person's (owner 2026-09-20). Same pink either way, but the word now says whose turn it is —
  // "Checking" on a row that has been sitting for a day reads as the AI being slow, when in fact
  // it is waiting on you.
  if (it.awaiting_answer) return 'AI Waiting';
  const base = STATUS_TEXT[it.status] || it.status;
  const n = Number(it.open_count) || 1;
  return it.status === 'waiting' && n > 1 ? `${base} - ${n}` : base;
};

const Pill = ({ it, deploying }: { it: { status: string; awaiting_answer: boolean; open_count?: number }; deploying?: boolean }) => {
  const k = lookOf(it, deploying);
  return (
    <span style={{ fontSize: 11, fontWeight: 800, color: k.fg, background: k.bg, border: `1px solid ${k.fg}33`, borderRadius: 999, padding: '2px 9px', whiteSpace: 'nowrap' }}>
      {it.awaiting_answer && !deploying && <i className="ti ti-help-circle" style={{ fontSize: 11, marginRight: 3, verticalAlign: '-1px' }} />}
      {statusText(it, deploying)}
    </span>
  );
};

// ── How far along, by stage (Calem 2026-09-28) ──────────────────────────────────────────────────
// Calem asked for a percentage complete on every report, out of 100. The AI cannot measure how far
// through a fix it is — writing the change is one long step with no counter inside it — so the figure
// is the STAGE the report has reached, which is what was offered on the report and accepted: an
// honest 50%, not a guessed 63%. AI Waiting shows checking's 20 (the AI has read it and is blocked on
// an answer). Won't Fix gets no figure: it is closed, not complete, and any number would claim one.
const STAGE_PCT: Record<string, number> = {
  waiting: 0, checking: 20, fixing: 50, needs_approval: 70, deploying: 85, ai_done: 90, confirmed: 100,
};
const stagePct = (it: { status: string; awaiting_answer: boolean }, deploying: boolean): number | null =>
  deploying ? STAGE_PCT.deploying : it.awaiting_answer ? STAGE_PCT.checking : (STAGE_PCT[it.status] ?? null);
// The bar's tooltip: every stage and its figure, one per line.
const STAGE_HINT = Object.entries(STAGE_PCT)
  .map(([s, p]) => `${s === 'checking' ? 'AI Checking / AI Waiting' : STATUS_TEXT[s]} ${p}%`).join('\n');

// ── The agent's heartbeat — read by the chip and by the rows ────────────────────────────────────
type Agent = { online: boolean; last_scan: number; state: string; current: string; note: string };
// Deploying is not a status the server stores: a round's fixes stay 'fixing' until their deploy is
// over, and an approved change stays 'needs_approval' until it has shipped. Only the heartbeat says
// it — 'deploying' just before a round deploys, and 'applying an approved change — …' with the
// report's id before an approved change is put back on and shipped on its own.
const APPLYING = 'applying an approved change';
const isDeploying = (it: { id: string; status: string }, a: Agent | null) =>
  !!a && ((it.status === 'fixing' && a.note === 'deploying') || (a.current === it.id && a.note.startsWith(APPLYING)));
// Changes exactly when some report starts or stops counting as Deploying.
const deploySignal = (a: Agent | null) =>
  !a ? '' : a.note === 'deploying' ? a.note : a.note.startsWith(APPLYING) ? `${APPLYING} ${a.current}` : '';
/** Fetch the reports again when a deploy starts or ends. The heartbeat is polled and the reports are
 *  not, so without this a report seen at Deploying 85% would drop back to the Fixing 50% it was
 *  loaded as once the deploy ended — when by then it is AI Done. */
function useDeployReload(a: Agent | null, reload: () => void) {
  const signal = deploySignal(a);
  const seen = useRef(signal);
  useEffect(() => {
    if (signal === seen.current) return;
    seen.current = signal;
    reload();
  }, [signal, reload]);
}
/** The same for the deploy record's signal (Release.tsx releaseSignal). Its first value ('' → the first answer) is
 *  where it starts from, not a change. */
function useSignalReload(signal: string, reload: () => void) {
  const seen = useRef(signal);
  useEffect(() => {
    if (signal === seen.current) return;
    const first = seen.current === '';
    seen.current = signal;
    if (!first) reload();
  }, [signal, reload]);
}

/** The status word and, after it, the stage figure as a small bar — on each row and in the header.
 *  `reserve`: in the list, a report with no figure (Won't Fix) keeps the bar's space, so the status
 *  words still line up down the right-hand side. */
function Status({ it, agent, reserve }: { it: Item; agent: Agent | null; reserve?: boolean }) {
  const { t } = useTranslation();
  const deploying = isDeploying(it, agent);
  const pct = stagePct(it, deploying);
  const k = lookOf(it, deploying);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
      <Pill it={it} deploying={deploying} />
      {pct !== null ? (
        <span title={t('bg_progress_hint', { stages: STAGE_HINT })} style={{ width: 74, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <span style={{ flex: 1, height: 6, borderRadius: 999, background: `${k.fg}26`, overflow: 'hidden' }}>
            <span style={{ display: 'block', width: `${pct}%`, height: '100%', background: k.fg }} />
          </span>
          <span style={{ width: 30, textAlign: 'right', fontSize: 11, fontWeight: 800, color: k.fg, fontVariantNumeric: 'tabular-nums' }}>{pct}%</span>
        </span>
      ) : reserve ? <span style={{ width: 74 }} /> : null}
    </span>
  );
}

const btn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 13px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', color: 'var(--text-primary)', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const inp: React.CSSProperties = { width: '100%', padding: '9px 11px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', color: 'var(--text-primary)', fontSize: 13.5, boxSizing: 'border-box' };

// A dialog in the full sense (2026-11-03, with the Confirm question): it is announced as one, Escape closes it, Tab
// stays inside it, and focus goes back to the button that opened it. `busy`: a request it started is on its way, so
// nothing closes it — not the backdrop, not ×, not Escape. `dirty`: something has been typed into it, which Escape
// must not throw away (the backdrop and × are deliberate enough).
const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';
function Modal({ title, onClose, children, wide, busy, dirty }: { title: string; onClose: () => void; children: React.ReactNode; wide?: boolean; busy?: boolean; dirty?: boolean }) {
  const { t } = useTranslation();
  const titleId = useId();
  const box = useRef<HTMLDivElement | null>(null);
  // Taken while rendering, before a child's autoFocus moves the focus into the dialog.
  const [opener] = useState(() => (typeof document !== 'undefined' ? document.activeElement as HTMLElement | null : null));
  useEffect(() => {
    if (box.current && !box.current.contains(document.activeElement)) box.current.focus();
    return () => { try { opener?.focus?.(); } catch { /* the opener is gone */ } };
  }, [opener]);
  const close = () => { if (!busy) onClose(); };
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.defaultPrevented) return;   // an editor inside used the key itself (a list's Tab, a menu's Escape)
    if (e.key === 'Escape') { e.stopPropagation(); if (!dirty) close(); return; }
    if (e.key !== 'Tab' || !box.current) return;
    const list = Array.from(box.current.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(el => el.offsetParent !== null);
    if (!list.length) { e.preventDefault(); return; }
    const first = list[0], last = list[list.length - 1], active = document.activeElement;
    if (e.shiftKey && (active === first || active === box.current)) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
  };
  return (
    <div onClick={close} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.42)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60, padding: 20 }}>
      <div ref={box} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} onKeyDown={onKeyDown} onClick={e => e.stopPropagation()}
        style={{ width: wide ? 720 : 460, maxWidth: '100%', maxHeight: '90vh', overflow: 'auto', background: 'var(--bg-app)', borderRadius: 14, boxShadow: '0 18px 48px rgba(15,23,42,0.28)', outline: 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '13px 18px', borderBottom: '1px solid var(--border)' }}>
          <div id={titleId} style={{ fontSize: 15, fontWeight: 800, color: 'var(--text-primary)' }}>{title}</div>
          <button onClick={close} disabled={busy} aria-label={t('bg_cancel')} style={{ border: 'none', background: 'transparent', cursor: busy ? 'default' : 'pointer', color: 'var(--text-tertiary)', fontSize: 18, opacity: busy ? 0.4 : 1 }}><i className="ti ti-x" aria-hidden /></button>
        </div>
        <div style={{ padding: 18 }}>{children}</div>
      </div>
    </div>
  );
}

// ── Refresh (Calem, report e25d54a2) ────────────────────────────────────────────────────────────
// Petty Cash's and Outbox's bare 32px icon, on the list and on a report. It turns while what it shows
// is being fetched, so a click that changes nothing still shows that something happened. Never
// disabled: fetch() has no timeout, and a stalled request must not leave reloading the page as the
// only way out again.
function RefreshButton({ busy, onClick }: { busy: boolean; onClick: () => void }) {
  const { t } = useTranslation();
  return (
    <button onClick={onClick} title={t('refresh')}
      style={{ width: 32, height: 32, borderRadius: 'var(--radius-md)', border: 'none', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer' }}>
      <i className="ti ti-refresh" style={{ fontSize: 15, display: 'inline-block', animation: busy ? 'spin 1s linear infinite' : undefined }} aria-hidden />
    </button>
  );
}

// ── Body / comment rendering ────────────────────────────────────────────────────────────────────
// The stored HTML carries `<img src="/api/bugify/images/…">`, which the browser would fetch on its
// own — WITHOUT the Authorization header, so it would 401. Each image is therefore fetched here
// with the token and swapped for an object URL, which keeps the endpoint authenticated instead of
// opening it up or minting a ticket per picture that expires mid-read.
function RichBody({ html }: { html: string }) {
  const ref = useRef<HTMLDivElement | null>(null);
  // Screenshots are capped to a readable height in CSS; clicking one opens it at full size, which
  // is the whole point of attaching a screenshot in the first place.
  const [zoom, setZoom] = useState('');
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const urls: string[] = [];
    let alive = true;
    for (const img of Array.from(el.querySelectorAll('img'))) {
      const src = img.getAttribute('src') || '';
      if (!src.startsWith('/api/bugify/images/')) continue;
      api.bugifyImageBlob(src)
        .then(u => { if (!alive) { URL.revokeObjectURL(u); return; } urls.push(u); img.setAttribute('src', u); })
        .catch(() => { img.replaceWith(Object.assign(document.createElement('div'), { textContent: '🖼 (image unavailable)', style: 'color:var(--text-tertiary);font-size:12px' })); });
    }
    return () => { alive = false; for (const u of urls) URL.revokeObjectURL(u); };
  }, [html]);
  return (
    <>
      <div ref={ref} className="bugify-body"
        onClick={e => { const el = e.target as HTMLElement; if (el.tagName === 'IMG') setZoom((el as HTMLImageElement).src); }}
        style={{ fontSize: 13.5, lineHeight: 1.6, color: 'var(--text-primary)', wordBreak: 'break-word' }}
        dangerouslySetInnerHTML={{ __html: html || '' }} />
      {zoom && (
        <div className="bugify-lightbox" onClick={() => setZoom('')}>
          <img src={zoom} alt="" />
        </div>
      )}
    </>
  );
}

// ── "AI online / offline" chip ──────────────────────────────────────────────────────────────────
// Without this a queue nobody is draining looks exactly like a broken system, and people re-file
// the same report (owner: 这时候界面上应该显示 "AI 离线").
function AgentChip({ a }: { a: Agent | null }) {
  const { t } = useTranslation();
  const me = useStore(s => s.me);
  // The server is polled once a minute — by BugifyView, which shares the answer with the rows; the
  // clock is redrawn every second so "last scan" counts up live (owner: 时间需要按秒实时跳动). Two
  // intervals deliberately — fetching once a second would be sixty times the requests to show
  // something the browser can work out for itself. The clock stays in here so that it redraws the
  // chip every second, not the whole list.
  const [, tick] = useState(0);
  useEffect(() => {
    const clock = setInterval(() => tick(n => n + 1), 1000);
    return () => clearInterval(clock);
  }, []);
  if (!a) return null;
  // Owner 2026-11-05: the AI runs on a laptop that is sometimes simply shut, and "AI offline · last scan
  // 1h" sends the people waiting on a report into a panic. Everyone but an admin sees "AI busy — your
  // report is saved and will be handled in turn" instead, without the clock or the last task. Admins
  // still see the real state: a stopped agent must not hide from the people who can restart it (the
  // 2026-10-19 sign-out ran twelve hours behind a reassuring chip).
  const calm = !a.online && me?.role !== 'admin';
  const fg = a.online ? '#15803D' : calm ? '#B45309' : '#6B7280';
  const note = calm ? '' : a.note;
  return (
    // The note is the root cause the agent is working on — a sentence, not a word — and a single
    // line cut it off mid-word (owner: 后面的文字说明需要显示完整…显示多行). With something to say
    // the chip takes a whole line of its own (flex-basis 100% forces the wrap) and grows downwards;
    // with nothing to say it shrinks back to a compact pill and sits inline. `minWidth: 0` is what
    // actually lets a flex child be narrower than its own text.
    <div title={a.online || calm ? '' : t('bg_ai_offline_hint')}
      style={{ display: 'flex', alignItems: 'flex-start', flexWrap: 'wrap', gap: 7, minWidth: 0,
        flex: note ? '1 1 100%' : '0 1 auto',
        fontSize: 12, fontWeight: 700, color: fg, background: a.online ? '#E8F6EE' : calm ? '#FEF3E2' : '#F3F4F6',
        border: `1px solid ${fg}33`, borderRadius: note ? 12 : 999, padding: note ? '7px 12px' : '4px 11px', lineHeight: 1.55 }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: fg, display: 'inline-block', marginTop: 5, flexShrink: 0 }} />
      <span style={{ flexShrink: 0, whiteSpace: 'nowrap' }}>{a.online ? t('bg_ai_online') : calm ? t('bg_ai_busy') : t('bg_ai_offline')}</span>
      <span style={{ fontWeight: 500, color: 'var(--text-tertiary)', flexShrink: 0, whiteSpace: 'nowrap' }}>
        · {calm ? t('bg_ai_busy_hint') : a.last_scan ? t('bg_ai_scanned', { when: relTime(a.last_scan) }) : t('bg_ai_never')}
      </span>
      {note && (
        <span style={{ fontWeight: 500, color: 'var(--text-tertiary)', minWidth: 0, flex: '1 1 260px', whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
          · {note}
        </span>
      )}
    </div>
  );
}

// ── What the AI changed ─────────────────────────────────────────────────────────────────────────
// Owner 2026-09-20: 将 fix 完毕的内容显示在 bugify 里，这样方便 office@slf-legal.co 进行审批.
// Shown for every fix, not only the ones awaiting approval — an approver has to have it, and
// everyone else gains from seeing what was done instead of taking "AI Done" on trust.
//
// Collapsed by default: a diff is long, and the report is what most people came to read.
function DiffView({ diff, files, title, open: openDefault }: { diff: string; files?: string[]; title: string; open?: boolean }) {
  const [open, setOpen] = useState(!!openDefault);
  if (!diff) return null;
  const lines = diff.split('\n');
  const added = lines.filter(l => l.startsWith('+') && !l.startsWith('+++')).length;
  const removed = lines.filter(l => l.startsWith('-') && !l.startsWith('---')).length;
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 10, marginTop: 10, overflow: 'hidden' }}>
      <button onClick={() => setOpen(o => !o)}
        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', border: 'none', background: 'var(--bg-active)', cursor: 'pointer', fontSize: 12.5, fontWeight: 700, color: 'var(--text-secondary)', textAlign: 'left' }}>
        <i className={`ti ti-chevron-${open ? 'down' : 'right'}`} />
        <i className="ti ti-file-diff" />
        <span>{title}</span>
        <span style={{ fontWeight: 500, color: 'var(--text-tertiary)' }}>
          {(files || []).length ? `${files!.length} file(s) · ` : ''}<span style={{ color: '#15803D' }}>+{added}</span> <span style={{ color: '#B91C1C' }}>−{removed}</span>
        </span>
      </button>
      {open && (
        <pre style={{ margin: 0, padding: '10px 12px', overflowX: 'auto', fontSize: 11.5, lineHeight: 1.5, background: 'var(--bg-surface)', maxHeight: 460, overflowY: 'auto' }}>
          {lines.map((l, i) => (
            <div key={i} style={{
              whiteSpace: 'pre', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              color: l.startsWith('+') && !l.startsWith('+++') ? '#15803D'
                : l.startsWith('-') && !l.startsWith('---') ? '#B91C1C'
                : l.startsWith('@@') ? '#6D28D9' : 'var(--text-secondary)',
              background: l.startsWith('+') && !l.startsWith('+++') ? 'rgba(21,128,61,0.07)'
                : l.startsWith('-') && !l.startsWith('---') ? 'rgba(185,28,28,0.07)' : undefined,
            }}>{l || ' '}</div>
          ))}
        </pre>
      )}
    </div>
  );
}

// ── Attachments ─────────────────────────────────────────────────────────────────────────────────
// Owner 2026-09-20, reversing "no attachments": Office, PDF, screenshots, text — 数据类的文件都支持.
// Deliberately NOT part of the rich-text body: an attachment is a file someone downloads, an image
// is prose. Mixing them would mean a spreadsheet rendering as a broken picture.
const fmtSize = (n: number) => (!n ? '' : n < 1024 ? `${n} B` : n < 1048576 ? `${(n / 1024).toFixed(0)} KB` : `${(n / 1048576).toFixed(1)} MB`);
const attIcon = (name: string) => {
  const e = (name.split('.').pop() || '').toLowerCase();
  if (e === 'pdf') return 'file-type-pdf';
  if (['xls', 'xlsx', 'csv', 'tsv', 'numbers', 'ofx', 'qbo'].includes(e)) return 'file-spreadsheet';
  if (['doc', 'docx', 'pages', 'rtf', 'odt'].includes(e)) return 'file-text';
  if (['ppt', 'pptx', 'key'].includes(e)) return 'presentation';
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'heic'].includes(e)) return 'photo';
  if (['zip', 'rar', '7z', 'gz'].includes(e)) return 'file-zip';
  if (['txt', 'log', 'json', 'xml', 'md'].includes(e)) return 'file-description';
  return 'paperclip';
};
const attRow: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, background: 'var(--bg-active)', border: '1px solid var(--border)', borderRadius: 8, padding: '4px 9px', maxWidth: '100%' };

/** Files staged while composing, before the row they belong to exists. */
function useAttachments() {
  const [files, setFiles] = useState<{ id: string; name: string; size: number }[]>([]);
  const [busy, setBusy] = useState(false);
  const add = async (list: FileList | File[] | null) => {
    const picked = Array.from(list || []);
    if (!picked.length) return;
    setBusy(true);
    for (const f of picked) {
      try { const r: any = await api.bugifyUploadAttachment(f); setFiles(x => [...x, { id: r.id, name: r.name, size: r.size }]); }
      catch (e: any) { alert(`${f.name}: ${e.message}`); }
    }
    setBusy(false);
  };
  const remove = (id: string) => setFiles(x => x.filter(f => f.id !== id));
  const ids = () => files.map(f => f.id);
  const clear = () => setFiles([]);
  return { files, add, remove, ids, clear, busy };
}

function AttachButton({ on, busy, label }: { on: (l: FileList | null) => void; busy: boolean; label: string }) {
  const ref = useRef<HTMLInputElement | null>(null);
  return (
    <>
      <button type="button" onClick={() => ref.current?.click()} disabled={busy} style={{ ...btn, padding: '6px 11px', fontSize: 12.5, opacity: busy ? 0.5 : 1 }}>
        <i className={`ti ti-${busy ? 'loader' : 'paperclip'}`} />{label}
      </button>
      <input ref={ref} type="file" multiple hidden onChange={e => { on(e.target.files); e.target.value = ''; }} />
    </>
  );
}

/** Staged files, with a way to take one back off. */
const StagedAtts = ({ files, onRemove }: { files: { id: string; name: string; size: number }[]; onRemove: (id: string) => void }) => (
  <>{files.map(f => (
    <span key={f.id} style={attRow}>
      <i className={`ti ti-${attIcon(f.name)}`} style={{ color: 'var(--text-tertiary)' }} />
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 220 }}>{f.name}</span>
      <span style={{ color: 'var(--text-tertiary)' }}>{fmtSize(f.size)}</span>
      <button type="button" onClick={() => onRemove(f.id)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-tertiary)', padding: 0, fontSize: 13 }}><i className="ti ti-x" /></button>
    </span>
  ))}</>
);

/** Saved attachments — one click downloads, fetched with the token. */
const SavedAtts = ({ atts }: { atts?: Att[] }) => (
  <>{(atts || []).map(a => (
    <button key={a.id} type="button" title={`${a.by} · ${fmtSize(a.size)}`}
      onClick={() => api.bugifyDownload(a.id, a.name).catch((e: any) => alert(e.message))}
      style={{ ...attRow, cursor: 'pointer', color: 'var(--accent)' }}>
      <i className={`ti ti-${attIcon(a.name)}`} />
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 260 }}>{a.name}</span>
      <span style={{ color: 'var(--text-tertiary)' }}>{fmtSize(a.size)}</span>
      <i className="ti ti-download" style={{ fontSize: 12 }} />
    </button>
  ))}</>
);

// ── Pasting a picture into the editor ───────────────────────────────────────────────────────────
// The editor cannot show `/api/bugify/images/<id>` directly: an <img src> is a request the browser
// makes on its own, with no Authorization header, so the image 401s and renders as a broken icon
// (owner reported exactly that). Stored bodies get around this by fetching each picture with the
// token — but that is for READING, and the editor is a live document.
//
// So while composing, the <img> points at an object URL made from the file already in hand — no
// round trip at all, it appears the instant it is pasted — and the canonical URL is swapped back in
// at save time. The document that leaves the browser therefore never contains a blob: URL, which
// matters: the server's sanitiser admits our own upload URLs and nothing else, so a blob that
// escaped would not be stored as a broken image, it would be dropped entirely.
function useInlineImages() {
  const ids = useRef<string[]>([]);
  const map = useRef<Map<string, string>>(new Map());   // object URL → /api/bugify/images/<id>
  useEffect(() => () => { for (const u of Array.from(map.current.keys())) URL.revokeObjectURL(u); }, []);

  const onImage = useCallback(async (f: File) => {
    const r: any = await api.bugifyUploadImage(f);
    const local = URL.createObjectURL(f);
    map.current.set(local, r.url);
    ids.current.push(r.id);
    return local;
  }, []);
  /** Canonicalise before sending: every object URL back to the URL the server will serve. */
  const finalise = useCallback((html: string) => {
    let out = html;
    for (const [local, real] of Array.from(map.current.entries())) out = out.split(local).join(real);
    return out;
  }, []);
  return { onImage, finalise, ids };
}

// ── Compose ─────────────────────────────────────────────────────────────────────────────────────
// One form for filing and for editing. The fields, the limits and the paste behaviour are the same
// in both cases; two copies would have drifted the first time one of them changed.
function NewReport({ onClose, onDone, edit }: { onClose: () => void; onDone: () => void; edit?: Item }) {
  // `i18n` from the SAME useTranslation call — a second one inside the useState initialiser below
  // would be a hook that runs on the first render only.
  const { t, i18n } = useTranslation();
  // Nothing preselected for a NEW report (owner 2026-10-08): opening on Bug meant a suggestion was
  // filed as a bug unless the reporter noticed and changed it, so the choice is asked for instead of
  // assumed — Submit stays off until one of the two is pressed. An edit opens on the report's own kind.
  const [kind, setKind] = useState(edit?.kind || '');
  const [lang, setLang] = useState(edit?.language || defaultLang((i18n.resolvedLanguage || i18n.language || 'en').slice(0, 2)));
  // A NEW report opens on P0 with Draft unticked (report 8ceb168b, 2026-10-12). This replaces the
  // 2026-09-21 rule of P1 with Draft ticked: Submit now hands the report to the AI at once, and a P0
  // is worked straight away, daytime included — P1/P2 or the Draft box are one click away for a
  // report that can wait. Editing keeps the report's own priority and draft state.
  const [priority, setPriority] = useState<number>(edit?.priority ?? 0);
  const [draft, setDraft] = useState(!!edit?.draft);
  const [title, setTitle] = useState(edit?.title || '');
  const [body, setBody] = useState(edit?.body || '');
  const [busy, setBusy] = useState(false);
  const [kept, setKept] = useState<Att[]>(edit?.attachments || []);
  const { onImage, finalise, ids: imageIds } = useInlineImages();
  const att = useAttachments();

  const dropSaved = async (a: Att) => {
    if (!edit || !window.confirm(t('bg_att_remove_confirm', { name: a.name }))) return;
    try { await api.bugifyRemoveAttachment(edit.id, a.id); setKept(x => x.filter(k => k.id !== a.id)); }
    catch (e: any) { alert(e.message); }
  };

  const save = async () => {
    if (busy) return;
    setBusy(true);
    const payload = { kind, title: title.trim(), body: finalise(body), language: lang, priority, draft, image_ids: imageIds.current, attachment_ids: att.ids() };
    try {
      if (edit) await api.bugifyEdit(edit.id, payload);
      else await api.bugifyCreate(payload);
      onDone(); onClose();
    }
    catch (e: any) { alert(e.message); }
    finally { setBusy(false); }
  };
  const left = TITLE_MAX - title.length;
  return (
    <Modal wide title={edit ? t('bg_edit') : t('bg_new')} onClose={onClose} busy={busy} dirty={!!title.trim() || !!body.replace(/<(?!img\b)[^>]*>/gi, '').trim() || att.files.length > 0}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 18, flexWrap: 'wrap', marginBottom: 6 }}>
        {([
          // The ask sits beside the KIND label only while neither segment is pressed — once it is
          // answered the question is furniture.
          [t('bg_kind'), <Seg key="k" options={[{ v: 'bug', label: `\u{1F41B} ${t('bg_kind_bug')}` }, { v: 'suggestion', label: `\u{1F4A1} ${t('bg_kind_suggestion')}` }]} value={kind} onChange={setKind} />, kind ? '' : t('bg_kind_ask')],
          [t('bg_priority'), <Seg key="p" options={[0, 1, 2].map(pv => ({ v: pv, label: PRIORITY_LOOK[pv].label, fg: PRIORITY_LOOK[pv].fg, bg: PRIORITY_LOOK[pv].bg }))} value={priority} onChange={setPriority} />, ''],
          [t('bg_language'), <Seg key="l" options={LANGS.map(l => ({ v: l.code, label: l.label }))} value={lang} onChange={setLang} />, ''],
        ] as [string, React.ReactNode, string][]).map(([label, control, ask]) => (
          <div key={label} style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <span style={{ fontSize: 10.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--text-tertiary)' }}>
              {label}
              {ask ? <span style={{ marginLeft: 6, fontWeight: 700, textTransform: 'none', letterSpacing: 0, color: '#B45309' }}>{ask}</span> : null}
            </span>
            {control}
          </div>
        ))}
      </div>
      {/* One sentence, about what is SELECTED — not a paragraph about everything selectable. */}
      <div style={{ fontSize: 11.5, color: PRIORITY_LOOK[priority].fg, marginBottom: 13 }}>
        {PRIORITY_LOOK[priority].label} — {t(`bg_p${priority}_hint`)}<span style={{ color: 'var(--text-tertiary)' }}> · {t('bg_language_hint')}</span>
      </div>
      <label style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.03em', color: 'var(--text-tertiary)' }}>{t('bg_title')}</label>
      <input autoFocus value={title} maxLength={TITLE_MAX} onChange={e => setTitle(e.target.value)} placeholder={t('bg_title_ph')} style={{ ...inp, margin: '5px 0 3px' }} />
      <div style={{ fontSize: 11, color: left <= 8 ? '#B45309' : 'var(--text-tertiary)', textAlign: 'right', marginBottom: 12 }}>{t('bg_title_left', { n: left })}</div>
      <label style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.03em', color: 'var(--text-tertiary)' }}>{t('bg_body')}</label>
      <div style={{ marginTop: 5 }}>
        <RichTextEditor value={body} onChange={setBody} placeholder={t('bg_body_ph')} minHeight={170} onImage={onImage} />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 7, marginTop: 10 }}>
        <AttachButton on={att.add} busy={att.busy} label={t('bg_attach')} />
        {kept.map(a => (
          <span key={a.id} style={attRow}>
            <i className={`ti ti-${attIcon(a.name)}`} style={{ color: 'var(--text-tertiary)' }} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 200 }}>{a.name}</span>
            <button type="button" onClick={() => dropSaved(a)} title={t('bg_att_remove')}
              style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-tertiary)', padding: 0, fontSize: 13 }}><i className="ti ti-x" /></button>
          </span>
        ))}
        <StagedAtts files={att.files} onRemove={att.remove} />
      </div>
      <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', marginTop: 6 }}>{t('bg_attach_hint')}</div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14, fontSize: 12.5, color: 'var(--text-secondary)', cursor: 'pointer' }}>
        <input type="checkbox" checked={draft} onChange={e => setDraft(e.target.checked)} style={{ width: 15, height: 15 }} />
        <span><b>{t('bg_draft')}</b> — {t('bg_draft_hint')}</span>
      </label>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
        <button onClick={onClose} style={btn}>{t('bg_cancel')}</button>
        <button onClick={save} disabled={busy || !title.trim() || !kind}
          style={{ ...btn, background: 'var(--accent)', color: '#fff', border: 'none', opacity: busy || !title.trim() || !kind ? 0.5 : 1 }}>
          <i className={`ti ti-${edit ? 'check' : 'send'}`} />{edit ? t('bg_save') : t('bg_submit')}
        </button>
      </div>
    </Modal>
  );
}

// ── Detail ──────────────────────────────────────────────────────────────────────────────────────
function Detail({ id, agent, release, onBack, onChanged, onRefresh }: { id: string; agent: Agent | null; release: ReleaseHook; onBack: () => void; onChanged: () => void; onRefresh: () => void }) {
  const { t, i18n } = useTranslation();
  const me = useStore(s => s.me);
  const [item, setItem] = useState<Item | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);
  const [ask, setAsk] = useState<'' | 'wont_fix' | 'waiting'>('');
  const [editing, setEditing] = useState(false);
  const [reason, setReason] = useState('');
  const [reloading, setReloading] = useState(false);
  // Confirm's question (Release.tsx ConfirmRelease): open, a choice on its way, what went wrong, and a notice after it
  // closed on an item somebody else had already confirmed.
  const [confirming, setConfirming] = useState(false);
  const [cBusy, setCBusy] = useState(false);
  const [cErr, setCErr] = useState('');
  const [notice, setNotice] = useState('');
  const { onImage, finalise, ids: imageIds } = useInlineImages();
  const att = useAttachments();

  // Returns the fetch so the refresh button can stop turning when it is back.
  // A load that failed because the server was restarting (apiDown) is made again once the API answers (slf:api-back) —
  // the same GET, once (review round 2: a report opened during the outage kept a bare loader after "Back online").
  const loadDown = useRef(false);
  const load = useCallback(() =>
    api.bugifyGet(id).then((d: any) => { loadDown.current = false; setItem(d.item); setComments(d.comments || []); })
      .catch((e: any) => { loadDown.current = !!e?.apiDown; }), [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const onBack = () => { if (loadDown.current) load(); };
    window.addEventListener('slf:api-back', onBack);
    return () => window.removeEventListener('slf:api-back', onBack);
  }, [load]);
  useDeployReload(agent, load);
  // A deploy that starts, ends or tries again changes this report's words ("goes out tonight" → "going out now" →
  // released) and its timeline: fetch it again then, quietly.
  useSignalReload(releaseSignal(release.info), load);

  if (!item) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-tertiary)' }}><i className="ti ti-loader" style={{ fontSize: 22 }} /></div>;

  const locked = item.status === 'fixing';
  // This report is in a deploy that is running, or in an immediate one that is about to start — then "goes out after
  // 20:00 tonight" is no longer true, and the card under the line shows what is happening instead.
  const ownRelease = [release.info?.current, release.info?.next].filter(v => !!v && v.state !== 'cancelled' && (v.item_ids || []).includes(item.id));
  const inRelease = ownRelease.some(v => v!.state === 'running' || (v!.mode === 'now' && (v!.state === 'requested' || v!.state === 'held')));
  // …and once this report's deploy has stopped (failed, lost, or blocked by something a person must fix), "after 20:00
  // tonight — nothing more to do" would contradict the card under it: the card alone says what happens next.
  const releaseStopped = ownRelease.some(v => v!.state === 'failed' || v!.state === 'lost'
    || (v!.state === 'blocked' && v!.code !== 'check_only' && v!.code !== 'stopped'));
  const iAmAsked = item.awaiting_answer && (item.asked_user === me?.userId || me?.role === 'admin');

  // Approve and roll back are REQUESTS: the repository is on the owner's Mac, so the agent carries
  // them out on its next round. Saying so is the difference between "nothing happened" and "it is
  // queued" (the agent polls every 8 seconds when idle, so it is usually seconds).
  const act = async (what: 'approve' | 'unapprove' | 'revert' | 'publish') => {
    if (busy) return;
    setBusy(true);
    try {
      await (what === 'approve' ? api.bugifyApprove(item.id)
        : what === 'unapprove' ? api.bugifyUnapprove(item.id)
        : what === 'publish' ? api.bugifyPublish(item.id)
        : api.bugifyRevert(item.id));
      load(); onChanged();
    }
    catch (e: any) { alert(e.message); }
    finally { setBusy(false); }
  };
  const setStatus = async (status: string, why?: string) => {
    if (busy) return;
    setBusy(true);
    try { await api.bugifyStatus(item.id, status, why); setAsk(''); setReason(''); load(); onChanged(); }
    catch (e: any) { alert(e.message); }
    finally { setBusy(false); }
  };
  // Confirm asks first (owner 2026-11-03): tonight, or now to every other system. A plain Confirm (no `release`) is what
  // the question falls back to when its options cannot be loaded — the server reads it as tonight.
  const openConfirm = () => { setCErr(''); setNotice(''); setConfirming(true); release.refresh(); };
  const confirmWith = async (mode?: 'now' | 'tonight') => {
    if (cBusy) return;
    setCBusy(true); setCErr('');
    try {
      await api.bugifyStatus(item.id, 'confirmed', undefined, mode);
      // The report and its deploy card drawn together — not the old card for a second under the new report.
      await Promise.all([load(), mode === 'now' ? release.fast() : release.refresh()]);
      setConfirming(false); onChanged();
    } catch (e: any) {
      if (e?.payload?.code === 'not_ai_done') {
        // Somebody got there first, or it moved on: close, show the report as it is now, and say why.
        setConfirming(false); load(); onChanged();
        setNotice(e.payload.status === 'confirmed' ? t('bgr_err_already', { who: e.payload.confirmed_by_name || '—' }) : t('bgr_err_not_ai_done'));
      } else setCErr(releaseErrorText(e, t, k => i18n.exists(k)));
    } finally { setCBusy(false); }
  };
  const deployNow = async () => {
    try { await api.bugifyReleaseNow(); await Promise.all([release.fast(), load()]); onChanged(); }
    catch (e: any) { alert(releaseErrorText(e, t, k => i18n.exists(k))); release.refresh(); }
  };
  const stopRelease = async (releaseId: string) => {
    try { await api.bugifyReleaseStop(releaseId); release.fast(); }
    catch (e: any) { alert(releaseErrorText(e, t, k => i18n.exists(k))); release.refresh(); }
  };
  const send = async () => {
    if (busy || !reply.trim()) return;
    setBusy(true);
    try { await api.bugifyComment(item.id, finalise(reply), imageIds.current, att.ids()); setReply(''); imageIds.current = []; att.clear(); load(); onChanged(); }
    catch (e: any) { alert(e.message); }
    finally { setBusy(false); }
  };
  // This report and its comments again — and the AI's heartbeat, which the status pill reads too.
  // A reply being typed and its staged files are left alone.
  const refresh = () => { setReloading(true); load().finally(() => setReloading(false)); onRefresh(); };

  return (
    <div style={{ flex: 1, overflow: 'auto', padding: '16px 22px 40px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
        <button onClick={onBack} style={btn}><i className="ti ti-arrow-left" />{t('bg_all')}</button>
        <RefreshButton busy={reloading} onClick={refresh} />
      </div>

      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 6 }}>
        <div style={{ flex: 1, fontSize: 20, fontWeight: 800, color: 'var(--text-primary)', textDecoration: item.status === 'wont_fix' ? 'line-through' : 'none' }}>{item.title}</div>
        <Status it={item} agent={agent} />
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 14, display: 'flex', flexWrap: 'wrap', gap: 10 }}>
        <span><i className={`ti ti-${item.kind === 'bug' ? 'bug' : 'bulb'}`} style={{ marginRight: 4 }} />{item.kind === 'bug' ? t('bg_kind_bug') : t('bg_kind_suggestion')}</span>
        <span>{t('bg_reported_by', { who: item.created_by_name, when: fmtBkk(item.created_at) })}</span>
        <PriBadge p={item.priority} />
        {item.night_hold && <span><i className="ti ti-moon" style={{ marginRight: 3 }} />{t('bg_tonight')}</span>}
        {item.language && <span><i className="ti ti-language" style={{ marginRight: 3 }} />{LANGS.find(l => l.code === item.language)?.label || item.language}</span>}
        {item.found_version && <span>{t('bg_found_version')} <b>{item.found_version}</b></span>}
        {item.edited && <span title={fmtBkk(item.updated_at)}><i className="ti ti-pencil" style={{ marginRight: 3 }} />{t('bg_edited', { who: item.updated_by_name, when: fmtBkk(item.updated_at) })}</span>}
        {item.fixed_version && <span>{t('bg_fixed_version')} <b>{item.fixed_version}</b>{item.reverted ? ` (${t('bg_reverted')})` : ''}</span>}
      </div>

      {/* The two states that need a person, spelled out rather than left to the colour alone. */}
      {item.awaiting_answer && (
        <div style={{ background: LOOK.asked.bg, border: `1px solid ${LOOK.asked.fg}33`, color: LOOK.asked.fg, borderRadius: 10, padding: '10px 13px', fontSize: 13, fontWeight: 700, marginBottom: 14 }}>
          <i className="ti ti-help-circle" style={{ marginRight: 6 }} />{iAmAsked ? t('bg_asked_you') : t('bg_asked')}
        </div>
      )}
      {item.status === 'ai_done' && (
        <div style={{ background: LOOK.ai_done.bg, border: `1px solid ${LOOK.ai_done.fg}33`, color: LOOK.ai_done.fg, borderRadius: 10, padding: '10px 13px', fontSize: 13, marginBottom: 14 }}>
          <i className="ti ti-flask" style={{ marginRight: 6 }} />{t('bg_test_hint')}
        </div>
      )}
      {item.status === 'needs_approval' && (
        <div style={{ background: LOOK.needs_approval.bg, border: `1px solid ${LOOK.needs_approval.fg}33`, color: LOOK.needs_approval.fg, borderRadius: 10, padding: '11px 13px', fontSize: 13, marginBottom: 14 }}>
          <div style={{ fontWeight: 700 }}><i className="ti ti-shield-lock" style={{ marginRight: 6 }} />{t('bg_needs_approval')}</div>
          {item.patch_reason && <div style={{ marginTop: 5 }}>{t('bg_approval_why', { why: item.patch_reason })}</div>}
          <div style={{ marginTop: 5, fontSize: 12 }}>{t('bg_approval_who', { who: ((item as any).approvers || []).join(t('people_or')) || '—' })}</div>
          {item.approved_at ? <div style={{ marginTop: 5, fontWeight: 700 }}>{t('bg_approved_by', { who: item.approved_by_name, when: fmtBkk(item.approved_at) })}</div> : null}
          {/* Which code the diff was cut from, and whether the tree has moved since (owner 2026-09-23):
              a held change can wait hours, and the approver should know it is not necessarily a
              clean fit any more — the agent replays it three-way and rewrites it if it is not. */}
          {item.patch_base ? <div style={{ marginTop: 5, fontSize: 12 }}>{t('bg_patch_base', { sha: item.patch_base.slice(0, 7) })}{item.code_moved ? ` ${t('bg_code_moved')}` : ''}</div> : null}
          {/* Another held change edits the same file(s). Two approvals at once means the second diff
              is applied to code the first just changed — so approve one at a time; the server
              refuses the second while the first is queued, and the agent rewrites it afterwards. */}
          {item.overlaps?.length ? (
            <div style={{ marginTop: 7, background: '#FFFBEB', color: '#B45309', border: '1px solid #B4530933', borderRadius: 8, padding: '7px 10px', fontSize: 12 }}>
              <i className="ti ti-alert-triangle" style={{ marginRight: 5 }} />
              {item.overlaps.map(o => t(o.approved ? 'bg_overlap_approved' : 'bg_overlap_pending', { title: o.title, files: o.files.join(', ') })).join(' ')}
            </div>
          ) : null}
        </div>
      )}
      {item.revert_requested && (
        <div style={{ background: '#FFFBEB', border: '1px solid #B4530933', color: '#B45309', borderRadius: 10, padding: '10px 13px', fontSize: 13, marginBottom: 14 }}>
          <i className="ti ti-arrow-back-up" style={{ marginRight: 6 }} />{t('bg_revert_pending', { who: item.revert_by_name })}
        </div>
      )}
      {item.reverted && (
        <div style={{ background: LOOK.wont_fix.bg, color: 'var(--text-secondary)', borderRadius: 10, padding: '10px 13px', fontSize: 13, marginBottom: 14 }}>
          <i className="ti ti-history" style={{ marginRight: 6 }} />{t('bg_reverted_done', { v: item.fixed_version })}
        </div>
      )}
      {item.status === 'confirmed' && (
        <div style={{ background: LOOK.confirmed.bg, color: LOOK.confirmed.fg, borderRadius: 10, padding: '10px 13px', fontSize: 13, fontWeight: 700, marginBottom: 14 }}>
          <i className="ti ti-check" style={{ marginRight: 6 }} />{t('bg_confirmed_by', { who: item.confirmed_by_name, when: fmtBkk(item.confirmed_at) })}
          {/* Confirm has a second effect since 2026-10-09 — it is what releases the fix to the
              other companies' installations, tonight. Nobody on the floor would guess that from a
              button labelled Confirm, so the banner says it while it is still owed. */}
          {/* Since 2026-11-03 it can also go out NOW (Release.tsx): while it does, the line says so and the card below
              shows how far it is; until then "Deploy now" sits under the line for anyone who no longer wants to wait. */}
          {item.fanout_due && <div style={{ fontWeight: 400, marginTop: 5 }}>
            {inRelease ? t('bgr_fanout_now') : releaseStopped ? null : t('bg_fanout_due')}
            {/* Not while the card below shows this report's deploy: its own "Try again now" is the one to press. */}
            {!ownRelease.length && <div><DeployNowButton info={release.info} onDeployNow={deployNow} /></div>}
          </div>}
        </div>
      )}
      {item.status === 'confirmed' && <ReleaseCard itemId={item.id} info={release.info} onDeployNow={deployNow} onStop={stopRelease} />}
      {item.status === 'wont_fix' && (
        <div style={{ background: LOOK.wont_fix.bg, color: 'var(--text-secondary)', borderRadius: 10, padding: '10px 13px', fontSize: 13, marginBottom: 14 }}>
          <div style={{ fontWeight: 700 }}><i className="ti ti-ban" style={{ marginRight: 6 }} />{t('bg_wontfix_by', { who: item.wontfix_by_name, when: fmtBkk(item.wontfix_at) })}</div>
          {item.wontfix_reason && <div style={{ marginTop: 5 }}>{item.wontfix_reason}</div>}
          {/* A fix that already shipped is not undone by closing the record — say so, or people
              assume one click cleaned it up while the change is still running in production. */}
          {item.fixed_version && !item.reverted && (
            <div style={{ marginTop: 7, fontSize: 12, color: '#B45309' }}>⚠ {t('bg_fixed_version')} <b>{item.fixed_version}</b> — {t('bg_wontfix_shipped')}</div>
          )}
        </div>
      )}

      <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 12, padding: '14px 16px', marginBottom: 20 }}>
        <RichBody html={item.body || ''} />
        {!!item.attachments?.length && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
            <SavedAtts atts={item.attachments} />
          </div>
        )}
      </div>

      {/* Held change first and open by default — on this screen it IS the thing to read. */}
      {item.patch ? <DiffView diff={item.patch} files={item.patch_files} title={t('bg_patch_title')} open /> : null}
      {item.fix_diff ? <DiffView diff={item.fix_diff} title={t('bg_diff_title', { v: item.fixed_version || '' })} /> : null}

      {notice && (
        <div role="status" style={{ marginTop: 16, background: LOOK.ai_done.bg, border: `1px solid ${LOOK.ai_done.fg}33`, color: LOOK.ai_done.fg, borderRadius: 10, padding: '9px 13px', fontSize: 13 }}>
          <i className="ti ti-info-circle" style={{ marginRight: 6 }} aria-hidden />{notice}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 16, marginBottom: 22 }}>
        {/* A draft is invisible to everyone but its author until it is filed, so the act of filing
            it belongs on the report itself — not buried under Edit (owner 2026-09-21). */}
        {item.can_publish && (
          <button onClick={() => act('publish')} disabled={busy}
            style={{ ...btn, background: '#1D7C4D', color: '#fff', border: 'none' }}>
            <i className="ti ti-send" />{t('bg_publish')}
          </button>
        )}
        {item.can_approve && (
          <button onClick={() => act('approve')} disabled={busy}
            style={{ ...btn, background: LOOK.needs_approval.fg, color: '#fff', border: 'none' }}>
            <i className="ti ti-shield-check" />{t('bg_approve')}
          </button>
        )}
        {/* Approved, not yet shipped. The agent picks it up within minutes, but the button used to
            stay live and unchanged in that gap, so it read as "nothing happened" (owner 2026-09-21,
            after pressing it three times). */}
        {item.approval_pending && (
          <span style={{ ...btn, background: 'var(--bg-app)', color: 'var(--text-secondary)', border: '1px solid var(--border-strong)', cursor: 'default' }}>
            <i className="ti ti-clock" />{t('bg_approve_pending')}
          </span>
        )}
        {/* The one way out of "approved and queued" — a comment or a status change no longer voids an
            approval (that is how two were lost silently), so the approver withdraws it on purpose. */}
        {item.can_unapprove && (
          <button onClick={() => { if (window.confirm(t('bg_unapprove_confirm'))) act('unapprove'); }} disabled={busy}
            style={{ ...btn, color: '#B45309' }}>
            <i className="ti ti-shield-off" />{t('bg_unapprove')}
          </button>
        )}
        {item.can_revert && (
          <button onClick={() => { if (window.confirm(t('bg_revert_confirm', { v: item.fixed_version || item.fix_commit }))) act('revert'); }} disabled={busy}
            style={{ ...btn, color: '#B45309' }}>
            <i className="ti ti-arrow-back-up" />{t('bg_revert')}
          </button>
        )}
        <button onClick={openConfirm} disabled={locked || item.status !== 'ai_done' || busy}
          title={locked ? t('bg_locked') : ''}
          style={{ ...btn, background: LOOK.confirmed.fg, color: '#fff', border: 'none', opacity: locked || item.status !== 'ai_done' ? 0.4 : 1, cursor: locked || item.status !== 'ai_done' ? 'not-allowed' : 'pointer' }}>
          <i className="ti ti-check" />{t('bg_confirm')}
        </button>
        <button onClick={() => setAsk('waiting')} disabled={locked || item.status === 'waiting' || busy} title={locked ? t('bg_locked') : ''}
          style={{ ...btn, opacity: locked || item.status === 'waiting' ? 0.4 : 1, cursor: locked || item.status === 'waiting' ? 'not-allowed' : 'pointer' }}>
          <i className="ti ti-arrow-back-up" />{t('bg_reopen')}
        </button>
        <button onClick={() => setAsk('wont_fix')} disabled={locked || item.status === 'wont_fix' || busy} title={locked ? t('bg_locked') : ''}
          style={{ ...btn, color: '#B91C1C', opacity: locked || item.status === 'wont_fix' ? 0.4 : 1, cursor: locked || item.status === 'wont_fix' ? 'not-allowed' : 'pointer' }}>
          <i className="ti ti-ban" />{t('bg_wontfix')}
        </button>
        {item.can_edit && (
          <button onClick={() => setEditing(true)} style={btn}><i className="ti ti-pencil" />{t('bg_edit')}</button>
        )}
        {locked && <span style={{ alignSelf: 'center', fontSize: 12, color: 'var(--text-tertiary)' }}><i className="ti ti-lock" style={{ marginRight: 4 }} />{t('bg_locked')}</span>}
      </div>

      <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.03em', color: 'var(--text-tertiary)', marginBottom: 10 }}>{t('bg_timeline')}</div>
      {comments.map(c => (
        <div key={c.id} style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
          <div style={{ width: 30, height: 30, flexShrink: 0, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, color: c.by_ai ? '#6D28D9' : 'var(--text-secondary)', background: c.by_ai ? '#F5F3FF' : 'var(--bg-active)' }}>
            {/* The robot, or the writer's initial trimmed to its cap height so the flex box centres the capital itself (Bugify #836d4443, 2nd opening) */}
            {c.by_ai ? <i className="ti ti-robot" /> : <span style={{ textBoxTrim: 'trim-both', textBoxEdge: 'cap alphabetic' }}>{(c.created_by_name || '?').slice(0, 1).toUpperCase()}</span>}
          </div>
          {/* A 'change' bubble is the AI saying what it just did, in words — it sits immediately
              before the line announcing the deploy, and is tinted like the Fixing state so the eye
              lands on it while scrolling a long thread (owner 2026-09-22). */}
          <div style={{ flex: 1, background: c.kind === 'question' ? LOOK.asked.bg : c.kind === 'change' ? LOOK.fixing.bg : 'var(--bg-surface)', border: `1px solid ${c.kind === 'question' ? LOOK.asked.fg + '33' : c.kind === 'change' ? LOOK.fixing.fg + '33' : 'var(--border)'}`, borderLeft: c.kind === 'change' ? `3px solid ${LOOK.fixing.fg}` : undefined, borderRadius: 10, padding: '9px 12px' }}>
            <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', marginBottom: 3 }}>
              <b style={{ color: 'var(--text-secondary)' }}>{c.by_ai ? t('bg_ai') : c.created_by_name}</b> · {fmtBkk(c.created_at)}
            </div>
            <RichBody html={c.body} />
            {!!c.attachments?.length && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginTop: 8 }}><SavedAtts atts={c.attachments} /></div>
            )}
          </div>
        </div>
      ))}

      <div style={{ marginTop: 14 }}>
        <RichTextEditor value={reply} onChange={setReply} minHeight={70} onImage={onImage}
          placeholder={iAmAsked ? t('bg_answer_ph') : t('bg_comment_ph')} />
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 7, marginTop: 8 }}>
          <AttachButton on={att.add} busy={att.busy} label={t('bg_attach')} />
          <StagedAtts files={att.files} onRemove={att.remove} />
          <div style={{ flex: 1 }} />
          <button onClick={send} disabled={busy || !reply.trim()} style={{ ...btn, background: 'var(--accent)', color: '#fff', border: 'none', opacity: busy || !reply.trim() ? 0.5 : 1 }}>
            <i className="ti ti-send" />{t('bg_send')}
          </button>
        </div>
      </div>

      {editing && <NewReport edit={item} onClose={() => setEditing(false)} onDone={() => { load(); onChanged(); }} />}
      {confirming && (
        <Modal title={t('bgr_modal_title')} busy={cBusy} onClose={() => setConfirming(false)}>
          <ConfirmRelease info={release.info} error={release.error} busy={cBusy} err={cErr} itemId={item.id} itemCommit={item.fix_commit || ''} onChoose={confirmWith} onCancel={() => setConfirming(false)} />
        </Modal>
      )}
      {ask && (
        <Modal title={ask === 'wont_fix' ? t('bg_wontfix') : t('bg_reopen')} onClose={() => { setAsk(''); setReason(''); }} busy={busy} dirty={!!reason.trim()}>
          <label style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.03em', color: 'var(--text-tertiary)' }}>{t('bg_reason')}</label>
          <textarea autoFocus value={reason} onChange={e => setReason(e.target.value)} rows={4}
            placeholder={ask === 'wont_fix' ? t('bg_reason_ph') : t('bg_reopen_ph')}
            style={{ ...inp, margin: '6px 0 14px', resize: 'vertical', fontFamily: 'inherit' }} />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button onClick={() => { setAsk(''); setReason(''); }} style={btn}>{t('bg_cancel')}</button>
            <button onClick={() => { if (ask === 'wont_fix' && !reason.trim()) return alert(t('bg_reason_required')); setStatus(ask, reason); }}
              disabled={busy || (ask === 'wont_fix' && !reason.trim())}
              style={{ ...btn, background: ask === 'wont_fix' ? '#B91C1C' : 'var(--accent)', color: '#fff', border: 'none', opacity: busy || (ask === 'wont_fix' && !reason.trim()) ? 0.5 : 1 }}>
              {ask === 'wont_fix' ? t('bg_wontfix') : t('bg_reopen')}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── List ────────────────────────────────────────────────────────────────────────────────────────
export function BugifyView() {
  const { t } = useTranslation();
  const [rows, setRows] = useState<Item[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(getPageSize());
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<'all' | 'open' | 'mine' | 'drafts'>('open');
  const [kind, setKind] = useState('');
  const [q, setQ] = useState('');
  const [sel, setSel] = useState('');
  const [newOpen, setNewOpen] = useState(false);
  // The agent's heartbeat, polled once a minute — here rather than inside the chip because the rows
  // need it too: it is the only thing that says a report is Deploying.
  const [agent, setAgent] = useState<Agent | null>(null);

  // `quiet` is a refresh nobody asked for (useDeployReload): no spinner, so the rows are swapped in
  // place and the scroll position is kept, and a failed fetch leaves the list as it was.
  // A load that failed because the server is restarting (apiDown) keeps the rows too: an empty "Nothing reported yet."
  // under "Back online — you can carry on" read as the reports being gone (review round 1, 2026-11-04). The list loads
  // again by itself as soon as the API answers (slf:api-back below) — a GET, sent once more, nothing else.
  // …but only the rows of the SAME query (rowsKey: tab, kind, search, page, page size). A tab, filter, search or page
  // changed during the outage clears them as before, or "Mine" would show the previous tab's reports (review round 2).
  const rowsKey = useRef('');
  const load = useCallback((quiet?: boolean) => {
    const key = JSON.stringify([tab, kind, q.trim(), page, limit]);
    if (!quiet) setLoading(true);
    api.bugifyList({ page, limit, open: tab === 'open', mine: tab === 'mine', drafts: tab === 'drafts', kind: kind || undefined, q: q.trim() || undefined })
      .then((d: any) => { rowsKey.current = key; setRows(d.rows || []); setTotal(d.total || 0); })
      .catch((e: any) => { if (!quiet && !(e?.apiDown && rowsKey.current === key)) { rowsKey.current = ''; setRows([]); setTotal(0); } })
      .finally(() => { if (!quiet) setLoading(false); });
  }, [page, limit, tab, kind, q]);
  useEffect(() => { const id = setTimeout(load, q ? 300 : 0); return () => clearTimeout(id); }, [load, q]);
  useEffect(() => {
    const onBack = () => load(true);
    window.addEventListener('slf:api-back', onBack);
    return () => window.removeEventListener('slf:api-back', onBack);
  }, [load]);
  useEffect(() => { setPage(1); }, [tab, kind, q]);
  // Outside its effect so that the refresh buttons can ask for the heartbeat as well.
  const alive = useRef(true);
  const pollAgent = useCallback(() => {
    api.bugifyAgent().then((d: any) => { if (alive.current) setAgent(d); }).catch(() => {});
  }, []);
  useEffect(() => {
    alive.current = true;
    pollAgent();
    const id = setInterval(pollAgent, 60_000);
    return () => { alive.current = false; clearInterval(id); };
  }, [pollAgent]);
  const refresh = useCallback(() => load(true), [load]);
  useDeployReload(agent, refresh);
  // The deploy to the other systems (Release.tsx): polled here, once, for the list's banner and the open report's card.
  const release = useRelease();
  useSignalReload(releaseSignal(release.info), refresh);

  if (sel) return <Detail id={sel} agent={agent} release={release} onBack={() => { setSel(''); load(); }} onChanged={() => { load(); release.refresh(); }} onRefresh={() => { pollAgent(); release.refresh(); }} />;

  const tabBtn = (k: 'all' | 'open' | 'mine' | 'drafts', label: string) => (
    <button key={k} onClick={() => setTab(k)}
      style={{ ...btn, borderColor: tab === k ? 'var(--accent)' : 'var(--border-strong)', background: tab === k ? 'var(--bg-active)' : 'var(--bg-surface)', color: tab === k ? 'var(--accent)' : 'var(--text-secondary)' }}>
      {label}
    </button>
  );

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap', padding: '12px 22px', borderBottom: '1px solid var(--border)' }}>
        {tabBtn('open', t('bg_open'))}{tabBtn('all', t('bg_all'))}{tabBtn('mine', t('bg_mine'))}{tabBtn('drafts', t('bg_drafts'))}
        <select value={kind} onChange={e => setKind(e.target.value)} style={{ ...btn, paddingRight: 8 }}>
          <option value="">{t('bg_kind')}</option>
          <option value="bug">{t('bg_kind_bug')}</option>
          <option value="suggestion">{t('bg_kind_suggestion')}</option>
        </select>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder={t('bg_search')} style={{ ...inp, width: 230 }} />
        {/* Beside the search box, as on the other pages (Calem: "Add a refresh/sync icon to the top menu
            rather than having to refresh the page"). Otherwise the list is fetched again only when a filter
            changes, after a change made here, or when a deploy starts or ends. It asks for the AI chip too,
            so the two agree. */}
        <RefreshButton busy={loading} onClick={() => { load(); pollAgent(); }} />
        <div style={{ flex: 1 }} />
        <button onClick={() => setNewOpen(true)} style={{ ...btn, background: 'var(--accent)', color: '#fff', border: 'none' }}>
          <i className="ti ti-plus" />{t('bg_new')}
        </button>
        {/* After the button on purpose: when the agent has something to say the chip claims a whole
            line, and coming last it wraps BELOW the controls instead of shoving them down a row. */}
        <AgentChip a={agent} />
      </div>
      <ReleaseBanner info={release.info} onOpen={setSel} />

      <div style={{ flex: 1, overflow: 'auto' }}>
        {loading ? <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-tertiary)' }}><i className="ti ti-loader" style={{ fontSize: 22 }} /></div>
          : rows.length === 0 ? <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>
              <i className="ti ti-bug" style={{ fontSize: 34, display: 'block', marginBottom: 8 }} />{q || kind || tab === 'mine' ? t('bg_empty_filter') : t('bg_empty')}
            </div>
          : rows.map(r => (
            <div key={r.id} onClick={() => setSel(r.id)}
              style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 22px', borderBottom: '1px solid var(--border)', cursor: 'pointer',
                // A pinned row is one the AI is blocked on and that THIS person was asked — it gets
                // the pink ground as well as the top of the list, so it cannot be scrolled past.
                background: r.pinned ? LOOK.asked.bg : undefined,
                borderLeft: r.pinned ? `3px solid ${LOOK.asked.fg}` : '3px solid transparent' }}>
              <i className={`ti ti-${r.kind === 'bug' ? 'bug' : 'bulb'}`} style={{ fontSize: 17, color: 'var(--text-tertiary)', flexShrink: 0 }} />
              <PriBadge p={r.priority} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text-primary)', textDecoration: r.status === 'wont_fix' ? 'line-through' : 'none', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.title}</div>
                <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {r.created_by_name} · {fmtBkk(r.created_at)}{r.found_version ? ` · ${r.found_version}` : ''}{r.preview ? ` — ${r.preview}` : ''}
                </div>
              </div>
              {r.night_hold && <span style={{ fontSize: 11, color: 'var(--text-tertiary)', whiteSpace: 'nowrap' }}><i className="ti ti-moon" style={{ marginRight: 3 }} />{t('bg_tonight')}</span>}
              {r.draft ? <span style={{ fontSize: 11, fontWeight: 800, color: 'var(--text-tertiary)', background: 'var(--bg-active)', borderRadius: 999, padding: '2px 9px' }}>{t('bg_draft')}</span> : <Status it={r} agent={agent} reserve />}
            </div>
          ))}
      </div>

      <Pagination page={page} total={total} limit={limit} onPage={setPage} onLimit={n => { setLimit(n); setPage(1); }} />
      {newOpen && <NewReport onClose={() => setNewOpen(false)} onDone={load} />}
    </div>
  );
}

export default function Bugify() {
  const { t } = useTranslation();
  // The day's AI improvement suggestions (report 1492665d), for the logins ticked to receive them: the way back
  // to the list once its once-a-day pop-up has been put down.
  const aiSuggestions = useStore(s => !!s.me?.aiSuggestions);
  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-app)' }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', borderBottom: '1px solid var(--border)', background: 'var(--bg-surface)' }}>
        <button onClick={() => window.location.assign('/')} title={brandOr('Home')} style={{ width: 30, height: 30, padding: 0, border: 'none', background: 'transparent', cursor: 'pointer' }}>
          <img className="app-logo" src="/logo.png" alt={brandOr('Logo')} style={{ width: 28, height: 28, objectFit: 'contain' }} />
        </button>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>{t('mod_bugify')}</div>
        {aiSuggestions && (
          <button onClick={openAiSuggestions} title={t('ais_title')}
            style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6, padding: '5px 12px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', color: 'var(--text-secondary)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            <i className="ti ti-bulb" style={{ fontSize: 14, color: 'var(--accent)' }} aria-hidden />
            {t('mod_ai_suggestions')}
          </button>
        )}
      </header>
      <BugifyView />
    </div>
  );
}
